import { gql, TypedDocumentNode } from "@apollo/client";
import type { ActivityType } from "@/pages/activities/activity-types/types";
import type { PDRHistory } from "@/types";

export type EducationCredentialType = "ASSOCIATE_DEGREE" | "BACHELORS_DEGREE";

export type EducationCredential = {
  id: string;
  legacyId?: string | null;
  sourceSystem: string;
  educationType: EducationCredentialType;
  description?: string | null;
  startedOn?: string | null;
  endedOn?: string | null;
  completedOn?: string | null;
  status: string;
  creditType?: string | null;
  creditsAwarded?: number | null;
  syncedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type EducationCredentialsQueryResponse = {
  person: {
    id: string;
    educationCredentials: EducationCredential[];
  };
};

export type EducationCredentialsQueryVariables = {
  pgaId: string;
};

export type PdrHistoryQueryResponse = {
  pdrHistoryPaginated: {
    nodes: Array<PDRHistory | null>;
    totalCount: number;
    totalCreditsAccepted: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
};

export type PdrHistoryQueryVariables = {
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  pgaId: string;
  cycleCode?: string | null;
  search?: string;
  creditTypes?: string[];
  fullTranscript?: boolean;
  activityDateFrom?: string;
  activityDateTo?: string;
  orderBy?: {
    field: string;
    direction: string;
  };
};

export type EligibleActivityTypesQueryResponse = {
  activityTypes: {
    edges: Array<{
      node: Pick<ActivityType, "id" | "title" | "description" | "createsEvent" | "sectionAvailability" | "config">;
    }>;
    totalCount: number;
  };
};

export type EligibleActivityTypesQueryVariables = {
  first?: number;
  filters?: {
    allowsParticipation?: boolean;
  };
};

export const GET_EDUCATION_CREDENTIALS_QUERY: TypedDocumentNode<
  EducationCredentialsQueryResponse,
  EducationCredentialsQueryVariables
> = gql`
  query GetEducationCredentials($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      educationCredentials {
        id
        legacyId
        sourceSystem
        educationType
        description
        startedOn
        endedOn
        completedOn
        status
        creditType
        creditsAwarded
        syncedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_EDUCATION_CERTIFICATION_HISTORY_QUERY = gql`
  query GetEducationCertificationHistory($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      educationLatestCertification {
        startDate
        endDate
        programName
        programDescription
        careerPath
        curriculumVersion
        subscription {
          startDate
          endDate
          productId
          expired
        }
      }
      educationCertificationHistory {
        startDate
        endDate
        programName
        programDescription
        careerPath
        curriculumVersion
        level
        levelCode
        completeCode
        remarks
        programRequirements {
          requireId
          levelCode
          completionStatus
          completionDate
          score
          isCompleted
          isInProgress
          requirementDefinition {
            levelCode
            description
            requireId
            requireType
          }
        }
        subscription {
          startDate
          endDate
          productId
          expired
        }
      }
    }
  }
`;

export type CanReaffiliateUniversityStudentQueryResponse = {
  person: {
    id: string;
    canReaffiliateUniversityStudent?: boolean | null;
  };
};

export type CanReaffiliateUniversityStudentQueryVariables = {
  pgaId: string;
};

export const GET_CAN_REAFFILIATE_UNIVERSITY_STUDENT_QUERY: TypedDocumentNode<
  CanReaffiliateUniversityStudentQueryResponse,
  CanReaffiliateUniversityStudentQueryVariables
> = gql`
  query GetCanReaffiliateUniversityStudent($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      canReaffiliateUniversityStudent
    }
  }
`;

export const GET_PGM_PROGRAM_HISTORY_QUERY = gql`
  query GetPgmProgramHistory($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      firstName
      lastName
      lastAttendedPgmUniversity {
        id
        legacyId
        name
      }
      educationLatestPgmProgram {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramCompleted
        pgmProgramExpirationDate
        programDurationYears
      }
      educationPgmProgramHistory {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramExpirationDate
        levelExpirationDate
        programRequirements {
          requireId
          notes
          remarks
          score
          levelCode
          completionStatus
          completionDate
          isCompleted
          isInProgress
          createDate
          requirementDefinition {
            levelCode
            courseDescription
            courseId
            description
            requireId
            requireType
          }
        }
      }
    }
  }
`;

export const GET_PDR_HISTORY_QUERY: TypedDocumentNode<PdrHistoryQueryResponse, PdrHistoryQueryVariables> = gql`
  query GetPdrHistory(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $pgaId: String!
    $cycleCode: String
    $search: String
    $creditTypes: [String!]
    $fullTranscript: Boolean
    $activityDateFrom: ISO8601Date
    $activityDateTo: ISO8601Date
    $orderBy: BackofficePdrPdrHistoryOrderByInput
  ) {
    pdrHistoryPaginated(
      first: $first
      last: $last
      after: $after
      before: $before
      pgaId: $pgaId
      cycleCode: $cycleCode
      search: $search
      creditTypes: $creditTypes
      fullTranscript: $fullTranscript
      activityDateFrom: $activityDateFrom
      activityDateTo: $activityDateTo
      orderBy: $orderBy
    ) {
      nodes {
        activityCode
        category
        creditType
        creditsAccepted
        creditsAppliedFor
        description
        id
        startedOn
        submittedAt
        activityParticipation {
          id
          title
          canEdit
          canDelete
          isTypeLevelParticipation
          activityType {
            id
            title
            description
            sectionAvailability
            createsEvent
          }
          activity {
            id
            title
            activityType {
              id
              title
              description
              sectionAvailability
              createsEvent
            }
            managingSection {
              id
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalCreditsAccepted
    }
  }
`;

export const GET_PDR_ACTIVITY_TYPES_QUERY: TypedDocumentNode<
  EligibleActivityTypesQueryResponse,
  EligibleActivityTypesQueryVariables
> = gql`
  query GetPdrActivityTypes($first: Int, $filters: ActivityTypeFiltersInput) {
    activityTypes(first: $first, filters: $filters) {
      edges {
        node {
          id
          title
          description
          createsEvent
          sectionAvailability
          config {
            creditRates {
              rateMultiplier
              rateUnit
              attendanceType
              participationRole
            }
          }
        }
      }
      totalCount
    }
  }
`;

export const GET_PDR_REQUIREMENTS_QUERY = gql`
  query getPdrRequirements($pgaId: ID!, $cycleCode: String!) {
    pdrRequirements(pgaId: $pgaId, cycleCode: $cycleCode) {
      pgaId
      cycleRequirements {
        cohort
        cohortCode
        cycleCode
        cycleStartsOn
        cycleEndsOn
        status
        requirements {
          pdpSerial
          label
          totalRequirement
          cycleRequirement
          earned
          deficit
          recoveryThreshold
          recoveryRemaining
          stipulationCode
        }
        requirementsEffectiveDate
        requirementsEndDate
        modifiedBy
      }
      cycleRequirementHistory {
        cohort
        cohortCode
        cycleCode
        cycleStartsOn
        cycleEndsOn
        status
        requirementsEffectiveDate
        requirementsEndDate
        modifiedBy
        requirements {
          pdpSerial
          label
          totalRequirement
          cycleRequirement
          earned
          deficit
          recoveryThreshold
          recoveryRemaining
          stipulationCode
        }
      }
      gateObligation {
        cohortCode
        originCycleCode
        eventDate
        satisfied
        stipulations {
          stipulationCode
          label
          required
          earned
          remaining
        }
        calculation {
          eventDate
          priorCohortCode
          priorCohortDescription
          priorClassification
          priorCycleCode
          buckets {
            stipulationCode
            label
            priorNormalRequirement
            prorationCredit
            earnedAtEvent
            deficitAdded
            frozenRequirement
          }
        }
      }
    }
  }
`;

export const GET_PDR_REQUIREMENT_CYCLE_OPTIONS_QUERY = gql`
  query getPdrRequirementCycleOptions {
    pdrRequirementCycleOptions {
      cycleCode
      startsOn
      endsOn
      requirementsEditable
    }
  }
`;

export const UPDATE_PDR_CREDIT_MUTATION = gql`
  mutation UpdatePdrCredit($input: UpdatePdrCreditInput!) {
    updatePdrCredit(input: $input) {
      message
      success
    }
  }
`;

export const DELETE_PDR_CREDIT_MUTATION = gql`
  mutation DeletePdrCredit($id: ID!) {
    deletePdrCredit(id: $id) {
      success
      message
    }
  }
`;

export const GET_PDR_ACTIVITY_CODES_QUERY = gql`
  query GetPdrActivityCodes($activityCode: String!) {
    pdrActivityCodes(activityCode: $activityCode) {
      activityCode
      description
    }
  }
`;

export const GET_PDR_DEFAULT_REQUIREMENT_STIPULATIONS_QUERY = gql`
  query DefaultRequirementStipulations(
    $pgaId: ID!
    $cycleCode: String!
    $effectiveDate: ISO8601Date
    $cohortCode: String
  ) {
    pdrDefaultRequirementStipulations(
      pgaId: $pgaId
      cycleCode: $cycleCode
      effectiveDate: $effectiveDate
      cohortCode: $cohortCode
    ) {
      success
      message
      cohortDiffersFromToday
      stipulations {
        stipulationCode
        deficit
        required
        label
        cycleCode
        cohortCode
        classification
        startsAt
        endsAt
      }
    }
  }
`;

export const UPSERT_PDR_REQUIREMENTS_MUTATION = gql`
  mutation UpsertPdrRequirements($input: BackofficeUpsertPdrRequirementsInput!) {
    upsertPdrRequirements(input: $input) {
      success
      message
      pdpRequirements {
        id
        author
        creditType
        creditAmount
      }
      requirement {
        cohortCode
        cycleCode
        id
        pgaId
      }
    }
  }
`;

export type TransferEducationProgramMutationResponse = {
  transferEducationProgram: {
    success: boolean;
    message: string;
    code: string;
  };
};

export type TransferEducationProgramMutationVariables = {
  input: {
    pgaId: string;
    levelCd: string;
  };
};

export const TRANSFER_EDUCATION_PROGRAM_MUTATION: TypedDocumentNode<
  TransferEducationProgramMutationResponse,
  TransferEducationProgramMutationVariables
> = gql`
  mutation TransferEducationProgram($input: BackofficeEducationTransferEducationProgramInput!) {
    transferEducationProgram(input: $input) {
      success
      message
      code
    }
  }
`;
